/**
 * Full-size photo modal. Backed by an already-resolved object URL so it
 * shows the bytes immediately when opened.
 *
 * Closes on Escape, on click outside the image, and on the explicit close
 * button. The caller revokes the object URL when it's done with it.
 */

import { useEffect, useState } from 'react';
import type { PhotoQuarterTurn } from '@cer-autofill/shared';
import { canRotatePhoto } from './EditablePhotoTile.js';
import { formatDateTime } from '../format.js';

export interface LightboxPhoto {
  photoId: string;
  blobUrl: string;
  promptKey: string;
  capturedAt: string;
  captureTimeUnknown?: boolean;
  filename: string;
  mimeType?: string;
  /** True when this preview URL is a blob the lightbox fetched itself and should revoke. */
  ownsBlob?: boolean;
}

/** Net clockwise quarter-turn from a running left/right tally. 0 means unchanged. */
export function quarterTurnDegrees(quarters: number): 0 | PhotoQuarterTurn {
  const turns = ((quarters % 4) + 4) % 4;
  if (turns === 1) return 90;
  if (turns === 2) return 180;
  if (turns === 3) return 270;
  return 0;
}

export function PhotoLightbox({
  photo,
  onClose,
  onSaveRotation,
  saving = false,
  saveError = null,
}: {
  photo: LightboxPhoto;
  onClose: () => void;
  /** Present for reviewers. Closing without saving discards the preview. */
  onSaveRotation?: (degrees: PhotoQuarterTurn) => Promise<void> | void;
  saving?: boolean;
  saveError?: string | null;
}) {
  const [zoom, setZoom] = useState(1);
  const [quarters, setQuarters] = useState(0);
  const [ratio, setRatio] = useState(1);
  const turns = quarterTurnDegrees(quarters);
  useEffect(() => {
    setZoom(1);
    setQuarters(0);
  }, [photo.blobUrl]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Photo preview"
    >
      <button
        type="button"
        className="lightbox__close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
      >
        Close ✕
      </button>
      <div className="lightbox__inner" onClick={(e) => e.stopPropagation()}>
        {photo.mimeType === 'application/pdf' ? (
          <a href={photo.blobUrl} target="_blank" rel="noopener noreferrer">
            Open {photo.filename} in a new tab
          </a>
        ) : (
          <>
            <label style={{ color: 'white' }}>
              Zoom {Math.round(zoom * 100)}%
              <input
                aria-label="Photo zoom"
                type="range"
                min="1"
                max="4"
                step="0.25"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </label>
            <div
              className="lightbox__viewport"
              style={{ width: `min(85vw, 1000px, ${70 * ratio}vh)` }}
            >
              <img
                src={photo.blobUrl}
                alt={photo.filename}
                onLoad={(e) =>
                  setRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight || 1)
                }
                style={{
                  width: `${zoom * 100}%`,
                  maxWidth: 'none',
                  maxHeight: 'none',
                  ...(turns === 0 ? {} : { transform: `rotate(${turns}deg)` }),
                }}
              />
            </div>
            {onSaveRotation && canRotatePhoto(photo.mimeType) && (
              <div className="lightbox__tools">
                <button
                  type="button"
                  disabled={saving}
                  aria-label="Rotate left"
                  onClick={() => setQuarters((value) => value - 1)}
                >
                  Rotate ↺
                </button>
                <button
                  type="button"
                  disabled={saving}
                  aria-label="Rotate right"
                  onClick={() => setQuarters((value) => value + 1)}
                >
                  Rotate ↻
                </button>
                <button
                  type="button"
                  disabled={saving || turns === 0}
                  onClick={() => {
                    if (turns === 0) return;
                    void onSaveRotation(turns);
                  }}
                >
                  {saving ? 'Saving…' : 'Save orientation'}
                </button>
              </div>
            )}
            {saveError && (
              <p role="alert" className="lightbox__error">
                {saveError}
              </p>
            )}
          </>
        )}
        <div className="lightbox__caption">
          <strong>{photo.promptKey}</strong> ·{' '}
          {photo.captureTimeUnknown ? 'Capture time unknown · added' : 'captured'}{' '}
          {formatDateTime(photo.capturedAt)} · {photo.filename}
        </div>
      </div>
    </div>
  );
}
